import { CURRENT_CLIENT } from '@/constants'
import {
  EMPLOYER_ORGANISATION_SETTINGS_BILLING_SEGMENT,
  employerOrganisationSettingsHubPath,
  employerOrganisationSettingsPath,
} from '@/constants/employer-organisation-settings-nav'

export type UpgradePlanConfig = {
  enabled: boolean
  label: string
  path: string
  externalUrl: string | null
  ctaHeadline: string
  ctaButtonLabel: string
}

type UpgradePlanFeature = {
  enabled?: boolean
  label?: string
  path?: string
  externalUrl?: string | null
  ctaHeadline?: string
  ctaButtonLabel?: string
}

export function employerOrganisationBillingPath(slug: string): string {
  return employerOrganisationSettingsPath(slug, EMPLOYER_ORGANISATION_SETTINGS_BILLING_SEGMENT)
}

/** Billing settings path for the active org, or the org-settings hub when slug is unknown. */
export function resolveUpgradePlanPath(organisationSlug: string | null | undefined): string {
  const trimmed = organisationSlug?.trim()
  if (trimmed) {
    return employerOrganisationBillingPath(trimmed)
  }
  return employerOrganisationSettingsHubPath(EMPLOYER_ORGANISATION_SETTINGS_BILLING_SEGMENT)
}

export function getUpgradePlanConfig(): UpgradePlanConfig | null {
  const raw = (CURRENT_CLIENT as { features?: { upgradePlan?: UpgradePlanFeature } }).features
    ?.upgradePlan
  if (raw?.enabled !== true) return null
  return {
    enabled: true,
    label: raw.label?.trim() || 'Plan & Billing',
    path: raw.path?.trim() || employerOrganisationSettingsHubPath(EMPLOYER_ORGANISATION_SETTINGS_BILLING_SEGMENT),
    externalUrl: raw.externalUrl?.trim() || null,
    ctaHeadline: raw.ctaHeadline?.trim() || 'Upgrade plan',
    ctaButtonLabel: raw.ctaButtonLabel?.trim() || 'Upgrade',
  }
}

export function shouldShowUpgradeCta(
  upgradePlan: UpgradePlanConfig | null,
  options?: {
    isAuthenticated?: boolean
    hasPaidPlan?: boolean
    isEmployerFocus?: boolean
  },
): upgradePlan is UpgradePlanConfig {
  if (!upgradePlan) return false
  if (options?.isAuthenticated === false) return false
  if (options?.isEmployerFocus !== true) return false
  if (options?.hasPaidPlan === true) return false
  return true
}

/** Account menu billing shortcut — paid orgs with a higher plan to upgrade to (not top tier). */
export function shouldShowBillingMenuItem(
  upgradePlan: UpgradePlanConfig | null,
  options?: {
    isAuthenticated?: boolean
    isEmployerFocus?: boolean
    organisationSlug?: string | null
    planLabel?: string | null
    hasPaidPlan?: boolean
    hasUpgradeTarget?: boolean
  },
): boolean {
  if (!upgradePlan) return false
  if (options?.isAuthenticated === false) return false
  if (options?.isEmployerFocus !== true) return false
  if (!options?.organisationSlug?.trim()) return false
  if (!options?.planLabel?.trim()) return false
  if (options?.hasPaidPlan !== true) return false
  if (options?.hasUpgradeTarget !== true) return false
  return true
}

/** Current org plan label in employer chrome — shown for free and paid plans. */
export function shouldShowEmployerPlanBadge(
  upgradePlan: UpgradePlanConfig | null,
  options?: {
    isAuthenticated?: boolean
    isEmployerFocus?: boolean
    organisationSlug?: string | null
  },
): boolean {
  if (!upgradePlan) return false
  if (options?.isAuthenticated === false) return false
  if (options?.isEmployerFocus !== true) return false
  return Boolean(options?.organisationSlug?.trim())
}

export function isUpgradePlanEnabled(): boolean {
  return getUpgradePlanConfig() != null
}
